import { useRef, useEffect } from 'react'
import dayjs from 'dayjs'
import { FiPrinter, FiX, FiEdit2 } from 'react-icons/fi'
import { formatString } from '../../../../utils'
import FormInput from '../../../../components/atoms/Input/Form-Input'
import Button from '../../../../components/atoms/Buttons'
import { getLatestStatus, getStatusLabel, getStatusBadgeClass, getActorFromHistory } from '../utils'
import { STATUS_SEQUENCE } from '../constants'
import { printOrder } from '../exportUtils'

const ModalHeader = ({ order, onPrint, onClose }) => (
    <div className="modal-header">
        <div>
            <h2>Order #{order.orderId}</h2>
            <span className="text-white/70 font-montserrat text-sm font-normal">
                {dayjs.unix(Number(order.createdAt)).format('MMMM D, YYYY [at] h:mm A')}
            </span>
        </div>
        <div className="flex items-center gap-3">
            <button onClick={onPrint} className="modal-close-btn" title="Print Order">
                <FiPrinter size={20} />
            </button>
            <button onClick={onClose} className="modal-close-btn">
                <FiX size={24} />
            </button>
        </div>
    </div>
)

const StatusBar = ({ order, isCactusAdmin, isEditMode, onEditClick }) => (
    <div className="flex items-center justify-between mb-6">
        <span className={`${getStatusBadgeClass(getLatestStatus(order))} text-[14px]`}>
            {getStatusLabel(getLatestStatus(order), isCactusAdmin)}
        </span>
        {!isEditMode && (
            <button
                onClick={onEditClick}
                className="action-btn action-btn-secondary flex items-center gap-2"
            >
                <FiEdit2 size={16} />
                Edit Status
            </button>
        )}
    </div>
)

const HorseMeasurements = ({ order }) => (
    <div className="detail-section">
        <h3 className="detail-section-title">Horse Measurements (Internal)</h3>
        <div className="detail-grid">
            <div className="detail-item measurement-item">
                <div className="detail-item-label">Gullet Width</div>
                <div className="detail-item-value">{order.gulletWidth || 'N/A'}</div>
            </div>
            <div className="detail-item measurement-item">
                <div className="detail-item-label">Bar Angle</div>
                <div className="detail-item-value">{order.barAngle || 'N/A'}°</div>
            </div>
        </div>
    </div>
)

const SADDLE_SPEC_FIELDS = [
    { label: 'Seat Size', key: 'seatSize', suffix: '"' },
    { label: 'Discipline', key: 'discipline', format: true },
]

const SaddleSpecifications = ({ saddleSpecs }) => (
    <div className="detail-section">
        <h3 className="detail-section-title">Saddle Specifications</h3>
        <div className="detail-grid">
            {SADDLE_SPEC_FIELDS.map(({ label, key, suffix, format }) => {
                const rawValue = saddleSpecs?.[key]
                const displayValue = rawValue
                    ? `${format ? formatString(rawValue) : rawValue}${suffix || ''}`
                    : 'N/A'

                return (
                    <div className="detail-item" key={key}>
                        <div className="detail-item-label">{label}</div>
                        <div className="detail-item-value">{displayValue}</div>
                    </div>
                )
            })}
        </div>
    </div>
)

const OrderTimeline = ({ order, isCactusAdmin }) => {
    const hasHistory = order.customOrderStatus?.length > 0

    if (!hasHistory) {
        return (
            <div className="detail-section">
                <h3 className="detail-section-title">Order Activity Log</h3>
                <div className="timeline-container">
                    <div className="timeline-item">
                        <div className="timeline-dot active" />
                        <div className="timeline-content">
                            <span className="timeline-status">Requested ~ System</span>
                            <span className="timeline-time">
                                {dayjs.unix(Number(order.createdAt)).format('MMMM D, YYYY [at] h:mm A')}
                            </span>
                        </div>
                    </div>
                </div>
            </div>
        )
    }

    const filteredHistory = [...order.customOrderStatus]
        .filter((h) => !(isCactusAdmin && (h.status === 'REQUESTED' || h.status === 'APPROVED')))
        .sort((a, b) => dayjs(b.timestamp).diff(dayjs(a.timestamp)))

    return (
        <div className="detail-section">
            <h3 className="detail-section-title">Order Activity Log</h3>
            <div className="timeline-container">
                {filteredHistory.map((history, index) => {
                    const actor = getActorFromHistory(history, order)
                    return (
                        <div key={`${history.timestamp}-${index}`} className="timeline-item">
                            <div className={`timeline-dot ${index === 0 ? 'active' : ''}`} />
                            <div className="timeline-content">
                                <span className="timeline-status">
                                    {getStatusLabel(history.status, isCactusAdmin)}
                                    <span className="text-gray-500 font-normal capitalize">
                                        {' '}~ {actor.name} {actor.name !== actor.role ? `(${actor.role})` : ''}
                                    </span>
                                </span>
                                <span className="timeline-time">
                                    {dayjs(history.timestamp).format('MMMM D, YYYY [at] h:mm A')}
                                </span>
                            </div>
                        </div>
                    )
                })}
            </div>
        </div>
    )
}

const ShippingAddress = ({ shippingAddress }) => (
    <div className="detail-section">
        <h3 className="detail-section-title">Shipping Address</h3>
        <div className="detail-item">
            <div className="detail-item-value">
                <p className="font-semibold">{shippingAddress?.fullName || 'N/A'}</p>
                <p className="text-gray-600">{shippingAddress?.addressLine1}</p>
                {shippingAddress?.addressLine2 && (
                    <p className="text-gray-600">{shippingAddress.addressLine2}</p>
                )}
                <p className="text-gray-600">
                    {[shippingAddress?.city, shippingAddress?.state, shippingAddress?.postalCode]
                        .filter(Boolean)
                        .join(', ')}
                </p>
                <p className="text-gray-600">{shippingAddress?.country}</p>
                {shippingAddress?.phone && (
                    <p className="text-gray-500 mt-2">📞 {shippingAddress.phone}</p>
                )}
            </div>
        </div>
    </div>
)

const AdminNotesSection = ({
    order,
    isEditMode,
    editStatus,
    setEditStatus,
    editNotes,
    setEditNotes,
    statusOptions,
    notesRef,
}) => (
    <div className="detail-section" ref={notesRef}>
        <h3 className="detail-section-title">Admin Notes</h3>
        {isEditMode ? (
            <div>
                <FormInput
                    name="status"
                    type="select2"
                    label="Update Status"
                    height={48}
                    options={statusOptions}
                    selectedValues={statusOptions.find((opt) => opt.value === editStatus)}
                    onChange={(option) => setEditStatus(option?.value)}
                    isOptionDisabled={(option) => {
                        if (option.isDisabled) return true
                        const currentStatus = getLatestStatus(order)
                        if (option.value === currentStatus) return true
                        const currentIndex = STATUS_SEQUENCE.indexOf(currentStatus)
                        const optionIndex = STATUS_SEQUENCE.indexOf(option.value)
                        if (currentIndex === -1 || optionIndex === -1) return false
                        return optionIndex < currentIndex
                    }}
                    className="mb-4"
                />
                <label className="block text-[12px] uppercase tracking-wide text-gray-500 font-semibold mb-2 font-montserrat">
                    Notes
                </label>
                <textarea
                    value={editNotes}
                    onChange={(e) => setEditNotes(e.target.value)}
                    placeholder="Add notes about this order..."
                    className="admin-notes-input"
                />
            </div>
        ) : (
            <div className="admin-notes-section font-montserrat">
                {order.adminNotes || 'No admin notes yet.'}
            </div>
        )}
    </div>
)

const ModalFooter = ({
    isEditMode,
    setIsEditMode,
    onUpdate,
    onClose,
    updateLoading,
    isDisabled,
}) => (
    <div className="modal-footer">
        {isEditMode ? (
            <>
                <button
                    onClick={() => setIsEditMode(false)}
                    className="action-btn action-btn-secondary"
                    disabled={updateLoading}
                >
                    Cancel
                </button>
                <Button
                    onClick={onUpdate}
                    loading={updateLoading}
                    disabled={isDisabled}
                    className="action-btn action-btn-primary"
                >
                    Save Changes
                </Button>
            </>
        ) : (
            <button onClick={onClose} className="action-btn action-btn-secondary">
                Close
            </button>
        )}
    </div>
)


const OrderDetailModal = ({
    order,
    isEditMode,
    setIsEditMode,
    editStatus,
    setEditStatus,
    editNotes,
    setEditNotes,
    onClose,
    onUpdate,
    updateLoading,
    statusOptions,
    printRef,
    isCactusAdmin,
}) => {
    const notesRef = useRef(null)

    useEffect(() => {
        if (isEditMode && notesRef.current) {
            setTimeout(() => {
                notesRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' })
            }, 100)
        }
    }, [isEditMode])

    const handlePrintOrder = () => printOrder(order, isCactusAdmin)
    const currentStatus = getLatestStatus(order)
    const isSaveDisabled =
        updateLoading || (editStatus === currentStatus && editNotes === (order.adminNotes || ''))

    return (
        <div className="order-detail-modal">
            <div className="modal-overlay" onClick={onClose} />
            <div className="modal-content" ref={printRef}>
                <ModalHeader order={order} onPrint={handlePrintOrder} onClose={onClose} />

                <div className="modal-body">
                    <StatusBar
                        order={order}
                        isCactusAdmin={isCactusAdmin}
                        isEditMode={isEditMode}
                        onEditClick={() => setIsEditMode(true)}
                    />
                    <HorseMeasurements order={order} />
                    <SaddleSpecifications saddleSpecs={order.saddleSpecs} />
                    <OrderTimeline order={order} isCactusAdmin={isCactusAdmin} />
                    <ShippingAddress shippingAddress={order.shippingAddress} />
                    <AdminNotesSection
                        order={order}
                        isEditMode={isEditMode}
                        editStatus={editStatus}
                        setEditStatus={setEditStatus}
                        editNotes={editNotes}
                        setEditNotes={setEditNotes}
                        statusOptions={statusOptions}
                        notesRef={notesRef}
                    />
                </div>

                <ModalFooter
                    isEditMode={isEditMode}
                    setIsEditMode={setIsEditMode}
                    onUpdate={onUpdate}
                    onClose={onClose}
                    updateLoading={updateLoading}
                    isDisabled={isSaveDisabled}
                />
            </div>
        </div>
    )
}

export default OrderDetailModal
